import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AlertTriangle, Package2, Users } from "lucide-react";

interface QuantityVarianceDisplayProps {
  invoicePackQty: number | null;
  invoiceUnits: number | null;
  receivedPackQty?: number | null;
  receivedUnits?: number | null;
  onResolve?: (action: 'use_invoice' | 'use_received' | 'investigate') => void;
  className?: string;
}

export function QuantityVarianceDisplay({
  invoicePackQty,
  invoiceUnits,
  receivedPackQty,
  receivedUnits,
  onResolve,
  className
}: QuantityVarianceDisplayProps) {
  const hasPackVariance = invoicePackQty && receivedPackQty && invoicePackQty !== receivedPackQty;
  const hasUnitVariance = invoiceUnits && receivedUnits && invoiceUnits !== receivedUnits;
  const hasAnyVariance = hasPackVariance || hasUnitVariance;

  if (!hasAnyVariance) {
    return (
      <div className={`space-y-1 ${className}`}>
        <div className="flex items-center space-x-2">
          <Package2 className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm">
            {invoicePackQty || 0} cases
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <Users className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm">
            {invoiceUnits || 0} units
          </span>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`space-y-1 ${className}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Package2 className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">
                  {invoicePackQty || 0} cases
                </span>
              </div>
              {hasPackVariance && (
                <Badge variant="destructive" className="text-xs">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Variance
                </Badge>
              )}
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">
                  {invoiceUnits || 0} units
                </span>
              </div>
              {hasUnitVariance && (
                <Badge variant="destructive" className="text-xs">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Variance
                </Badge>
              )}
            </div>
            
            {onResolve && hasAnyVariance && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="w-full">
                    Resolve Variance
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center">
                  <DropdownMenuItem onClick={() => onResolve('use_invoice')}>
                    Use invoice quantities
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onResolve('use_received')}>
                    Use received quantities
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onResolve('investigate')}>
                    Mark for investigation
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-1">
            <p className="font-medium">Quantity Comparison</p>
            <div className="text-xs space-y-1">
              <p>Invoice: {invoicePackQty} cases, {invoiceUnits} units</p>
              <p>Received: {receivedPackQty} cases, {receivedUnits} units</p>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}